import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaArrowLeft, FaArrowRight, FaPlay } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { GoMute, GoUnmute } from "react-icons/go";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow } from "swiper/modules";
import { getImageUrl } from "../../../../../constants/globalFunction";
import CommonButton from "../../../../../components/button/CommonButton";

import "swiper/css";
import "swiper/css/effect-coverflow";
import "./VideoModal.css";

const VideoModal = ({ videos, currentIndex, isOpen, onClose }) => {
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const videoRef = useRef(null);

  const currentVideo = videos?.[activeIndex];
  const videoUrl = currentVideo?.video ? getImageUrl(currentVideo.video) : null;
  const thumbnailUrl = currentVideo?.image 
    ? getImageUrl(currentVideo.image) 
    : (currentVideo?.thumbnail ? getImageUrl(currentVideo.thumbnail) : null);
  const hasUrl = currentVideo?.url;

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveIndex(currentIndex);
      setIsMuted(true);
      setProgress(0);
    }
  }, [isOpen, currentIndex]);

  // Play video when activeIndex or videoUrl changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl || !isOpen) return;

    // Set new source and play
    video.src = videoUrl;
    video.muted = isMuted;
    video.currentTime = 0;
    
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        video.muted = true;
        video.play().catch(() => {});
      });
    }
  }, [activeIndex, isOpen]);

  // Update mute state
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  // Video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (video.duration) {
        setProgress((video.currentTime / video.duration) * 100);
      }
    };

    const handleEnded = () => {
      setTimeout(() => {
        if (swiperInstance) {
          if (activeIndex < videos.length - 1) {
            swiperInstance.slideNext();
          } else {
            swiperInstance.slideTo(0);
          }
        }
      }, 300);
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("ended", handleEnded);
    };
  }, [swiperInstance, activeIndex, videos.length]);

  const handleSlideChange = useCallback((swiper) => {
    setActiveIndex(swiper.activeIndex);
    setProgress(0);
  }, []);

  const handleToggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  const handleViewProduct = useCallback(() => {
    if (currentVideo?.url) {
      window.open(currentVideo.url, "_blank");
    }
  }, [currentVideo]);

  const handlePrevious = useCallback(() => {
    swiperInstance?.slidePrev();
  }, [swiperInstance]);

  const handleNext = useCallback(() => {
    swiperInstance?.slideNext();
  }, [swiperInstance]);

  if (!isOpen || !videos || videos.length === 0) return null;

  return (
    <div className="video-modal-overlay" onClick={onClose}>
      <div className="video-modal-wrapper" onClick={(e) => e.stopPropagation()}>
        <button className="video-modal-close" onClick={onClose}>
          <IoClose size={30} />
        </button>

        {activeIndex > 0 && videos.length > 1 && (
          <button className="video-modal-nav video-modal-nav--prev" onClick={handlePrevious}>
            <FaArrowLeft />
          </button>
        )}

        <div className="video-carousel-container">
          <Swiper
            effect="coverflow"
            grabCursor={true}
            centeredSlides={true}
            slidesPerView="auto"
            initialSlide={currentIndex}
            speed={400}
            watchSlidesProgress={true}
            coverflowEffect={{
              rotate: 0,
              stretch: 0,
              depth: 150,
              modifier: 1.5,
              slideShadows: false,
            }}
            modules={[EffectCoverflow]}
            onSwiper={setSwiperInstance}
            onSlideChange={handleSlideChange}
            className="video-swiper"
          >
            {videos.map((video, index) => {
              const thumbUrl = video?.image 
                ? getImageUrl(video.image) 
                : (video?.thumbnail ? getImageUrl(video.thumbnail) : null);
              const isActive = index === activeIndex;
              
              return (
                <SwiperSlide key={video._id || index} className="video-swiper-slide">
                  <div className="video-slide-wrapper">
                    {/* Thumbnail image */}
                    {thumbUrl && (
                      <img 
                        src={thumbUrl} 
                        alt="" 
                        className="video-slide-thumb-img"
                        draggable={false}
                      />
                    )}
                    
                    {/* Dark overlay + play icon for non-active */}
                    {!isActive && (
                      <div className="video-slide-inactive-overlay">
                        <div className="video-slide-play-icon">
                          <FaPlay />
                        </div>
                      </div>
                    )}

                    {/* Active slide: video player overlay */}
                    {isActive && (
                      <div className="video-slide-active-content">
                        {/* Progress bar */}
                        <div className="video-modal-progress-bar">
                          <div 
                            className="video-modal-progress-fill" 
                            style={{ width: `${progress}%` }} 
                          />
                        </div>

                        {/* Video */}
                        <video
                          ref={videoRef}
                          className="video-slide-video"
                          playsInline
                          muted={isMuted}
                          preload="auto"
                        />

                        {/* Mute button */}
                        <button
                          className="video-modal-mute-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleMute();
                          }}
                        >
                          {isMuted ? <GoMute /> : <GoUnmute />}
                        </button>

                        {/* View button */}
                        {hasUrl && (
                          <div className="video-modal-actions">
                            <CommonButton
                              title="View"
                              onPress={handleViewProduct}
                              customClassName="video-modal-view-button"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>

        {activeIndex < videos.length - 1 && videos.length > 1 && (
          <button className="video-modal-nav video-modal-nav--next" onClick={handleNext}>
            <FaArrowRight />
          </button>
        )}
      </div>
    </div>
  );
};

export default VideoModal;
